// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

// ─────────────────────────────────────────────────────────────────────────────
// Client-side orchestrator (ADR-007 D1, amended by #35). The widget's own
// state machine drives the composed read: resolve → collect (D1 candidates +
// unconditional AU0001 peer read) → select (D3) → conditional enrich (D2,
// winner-only) → conditional offline log (D6, AU0001-winner-only). Each
// bridge round-trip is one stage record (ADR-007 D2) under one correlation id
// (ADR-007 D3). The orchestrator decides the next stage from the previous
// stage's terminal result, performs the ADR-004 D3 last-operation selection and
// D5 halt-and-report, and emits per-stage progress (ADR-007 D5).
//
// It holds NO privileged access and never becomes a server-side authority
// (ADR-002 P6 / ADR-006 D2): each stage still passes through the bridge, whose
// server side enforces authorization/scope per record.
// ─────────────────────────────────────────────────────────────────────────────

import { BridgeClient } from "@/bridge/client";
import type { BridgeRequestType } from "@/bridge/types";
import type {
  OfflineAttributionEntry,
  OfflineWinnerInfo,
  SelectedOperation,
} from "@/contracts/display";
import { isUnknown, toUnknownableString, UNKNOWN } from "@/contracts/display";
import type { QueryOutcome } from "@/contracts/outcome";
import type {
  CandidateOperation,
  CollectCandidatesResponse,
  EnrichMetaResponse,
  OfflineCandidateResponse,
  QueriedUpdaterAU,
  RecentOfflineBundlesResponse,
  ResolveDeviceResponse,
} from "@/contracts/requests";
import { guessIdentifierType } from "./identifier";
import type { StageId } from "./stages";

/**
 * ADR-004 D1/D3 eligibility filter (amended by #35): only a successful
 * AU0001 read competes in D3's comparison. The server already requests only
 * "success" entries (ProcessServiceRequest.ds passes exec_status="success" to
 * GetLatestAttributionByDeviceAnchor), so this is a defensive re-check, not
 * the only gate — the client never trusts response shape alone (ISO/IEC 5055).
 */
const AU0001_SUCCESS_LITERAL = "success";

/**
 * ADR-004 D6 (redefined by #35; #27) business rule for the offline-augment
 * listing: fixed to "offline update" records, most-recent-first, capped at 3
 * — decided client-side (ADR-007 D1: the resolver only relays, it does not
 * decide business rules), against ListFirmwarePackageMeta's real, confirmed
 * signature (deviceAnchor, updateMethod, tsBoundary, tsDirection, limit — all
 * required).
 */
const OFFLINE_UPDATE_METHOD = "offline update";
const OFFLINE_BUNDLE_LOG_LIMIT = 3;

export interface RunQueryHandlers {
  /** Fired as each stage becomes active / done / skipped / failed (ADR-007 D5). */
  onStage?: (
    id: StageId,
    state: "active" | "done" | "skipped" | "failed",
    detail?: string,
  ) => void;
  signal?: AbortSignal;
}

export async function runQuery(
  client: BridgeClient,
  identifier: string,
  handlers: RunQueryHandlers = {},
): Promise<QueryOutcome> {
  const correlationId = BridgeClient.newCorrelationId();
  const { onStage, signal } = handlers;

  // ── Stage 1: resolve (ADR-003) ──
  // Client-side identifier detection (ADR-003 D1): UUID shape → software id,
  // else serial number. The matching request kind is chosen here; the server
  // side still performs authoritative resolution (ADR-003 D2/D3).
  const resolveRequestType: BridgeRequestType =
    guessIdentifierType(identifier) === "software-id"
      ? "GetDeviceBySoftwareId"
      : "GetDeviceBySerialNumber";

  onStage?.("resolve", "active");
  const resolveRec = await submit<ResolveDeviceResponse>("resolve", () =>
    client.submitStage({
      correlationId,
      requestType: resolveRequestType,
      request: { identifier },
      ...(signal ? { signal } : {}),
    }),
  );
  if (resolveRec.kind === "halt") {
    onStage?.("resolve", "failed");
    return resolveRec.outcome;
  }
  const resolveResp = resolveRec.response;
  if (resolveResp.resolution === "unresolved") {
    onStage?.("resolve", "done");
    if (resolveResp.ambiguous === true) {
      return { kind: "halted", reason: "ambiguous" };
    }
    // Not-found and out-of-scope are indistinguishable by design (ADR-003 D3 /
    // ADR-006 D3): no existence disclosure.
    return { kind: "not_found_or_out_of_scope" };
  }
  const identity = resolveResp.identity;
  onStage?.("resolve", "done");

  // ── Stage 2: collect (ADR-004 D1) + unconditional AU0001 read (D3, #35) ──
  // Query each evidenced O1 Updater AU in turn. If a bridge call halts the
  // whole stage fails; a non-response from an individual AU is recorded in
  // queriedUpdaterAUs and collection moves on (ADR-004 D1). AU0001 is queried
  // in the same stage, unconditionally — it is a peer input to D3's
  // selection, not a D1 candidate and not gated on any updater's method.
  const updaterAuRequests: ReadonlyArray<{
    requestType: BridgeRequestType;
    channel: string;
  }> = [
    { requestType: "GetAU0008LatestAccountability", channel: "Update Firmware Over Serial" },
    { requestType: "GetAU0004LatestAccountability", channel: "Update Firmware over BLE" },
  ];

  const allCandidates: CandidateOperation[] = [];
  const allQueriedAUs: QueriedUpdaterAU[] = [];

  onStage?.("collect", "active", `Querying ${updaterAuRequests[0]!.channel}…`);

  for (let i = 0; i < updaterAuRequests.length; i++) {
    const au = updaterAuRequests[i]!;

    // Update detail for subsequent AUs before their call.
    if (i > 0) {
      onStage?.("collect", "active", `Querying ${au.channel}…`);
    }

    const collectRec = await submit<CollectCandidatesResponse>("collect", () =>
      client.submitStage({
        correlationId,
        requestType: au.requestType,
        request: { softwareId: softwareIdOf(identity) },
        ...(signal ? { signal } : {}),
      }),
    );

    if (collectRec.kind === "halt") {
      onStage?.("collect", "failed");
      return collectRec.outcome;
    }

    allCandidates.push(...collectRec.response.candidates);
    allQueriedAUs.push(...collectRec.response.queriedUpdaterAUs);
  }

  onStage?.("collect", "active", "Querying offline update log…");
  const au0001Rec = await submit<OfflineCandidateResponse>("collect", () =>
    client.submitStage({
      correlationId,
      requestType: "GetAU0001LatestAttributionByDeviceAnchor",
      request: { deviceAnchor: softwareIdOf(identity) },
      ...(signal ? { signal } : {}),
    }),
  );
  if (au0001Rec.kind === "halt") {
    onStage?.("collect", "failed");
    return au0001Rec.outcome;
  }
  const au0001Response = au0001Rec.response;

  // ── ADR-004 D3 (amended by #35): select before any enrichment ──
  const selection = selectLastOperation(allCandidates, au0001Response);
  onStage?.("collect", "done", selection?.tied ? "Tie-break: earliest-queried source kept." : undefined);

  if (selection === undefined) {
    // No eligible candidate from either source → D1's empty result (ADR-004
    // D1); an AU0001 read that is absent or non-eligible does not change this.
    onStage?.("enrich", "skipped");
    onStage?.("offline", "skipped");
    return { kind: "resolved_no_operation", identity, queriedUpdaterAUs: allQueriedAUs };
  }

  if (selection.source === "au0001") {
    // AU0001 won D3 — "effectively offline" is a RESULT of the comparison,
    // not a precondition (#35). D2 enrichment is skipped entirely (D3), since
    // FirmwareDeliveryAPIv1 has nothing to enrich by reference id for a
    // winner it never produced a package for.
    onStage?.("enrich", "skipped");

    // au0001Response.found is guaranteed true here: selectLastOperation only
    // returns { source: "au0001" } when the AU0001 read competed and won
    // (selectLastOperation's own eligibility check above).
    const offlineWinner: OfflineWinnerInfo = au0001Response.found
      ? {
          operationTs: toUnknownableString(au0001Response.operationTs),
          sourceFileName: toUnknownableString(au0001Response.sourceFileName),
          transferredBy: toUnknownableString(au0001Response.sourceTransferredBy),
          transferTime: toUnknownableString(au0001Response.sourceTransferTime),
        }
      : {
          operationTs: UNKNOWN,
          sourceFileName: UNKNOWN,
          transferredBy: UNKNOWN,
          transferTime: UNKNOWN,
        };

    // AU0001's fuller offline attribution history (provisional local display
    // addition — see OfflineAttributionEntry in ../contracts/display).
    // Advisory only, like offlineAugment below; not part of D3 selection.
    const offlineAttributionList: OfflineAttributionEntry[] = au0001Response.found
      ? (au0001Response.attributionList ?? []).map((entry) => ({
          executionStatus: toUnknownableString(entry.executionStatus),
          operationTs: toUnknownableString(entry.operationTs),
        }))
      : [];

    // ── D6 (redefined by #35; #27): last-3 offline bundle-creation records ──
    // Advisory context beneath the winner's own fields above — NOT part of
    // D3 selection, NOT proof of execution (README). tsBoundary anchors the
    // listing to the AU0001 winner's own (raw-format) operation time, so the
    // "last 3" are relative to the selected offline operation, not "now";
    // tsBoundary falls back to "" (the capability's own documented default —
    // its current time) only in the unreachable case where the winning
    // AU0001 read somehow carried no raw timestamp.
    onStage?.("offline", "active");
    const offlineRec = await submit<RecentOfflineBundlesResponse>("offline", () =>
      client.submitStage({
        correlationId,
        requestType: "ListFirmwarePackageMeta",
        request: {
          deviceAnchor: softwareIdOf(identity),
          updateMethod: OFFLINE_UPDATE_METHOD,
          tsBoundary:
            au0001Response.found && au0001Response.operationTsRaw !== undefined
              ? au0001Response.operationTsRaw
              : "",
          tsDirection: "before",
          limit: OFFLINE_BUNDLE_LOG_LIMIT,
          // Widget-carried from resolve (ADR-008 D2); routes this read to
          // app74/app21 server-side (ADR-008 D3).
          is_demo: isDemoOf(identity),
        },
        ...(signal ? { signal } : {}),
      }),
    );
    if (offlineRec.kind === "halt") {
      onStage?.("offline", "failed");
      return offlineRec.outcome;
    }
    onStage?.("offline", "done");

    return {
      kind: "resolved_shown",
      identity,
      offlineWinner,
      offlineAugment: offlineRec.response.bundles,
      offlineAttributionList,
      queriedUpdaterAUs: allQueriedAUs,
    };
  }

  // ── Conditional enrich: winner-only, AU0008/AU0004-sourced (ADR-004 D2) ──
  const winner = selection.candidate;
  onStage?.("enrich", "active");
  const enrichRec = await submit<EnrichMetaResponse>("enrich", () =>
    client.submitStage({
      correlationId,
      requestType: "GetFirmwarePackageMetaByReferenceId",
      // Widget-carried from resolve (ADR-008 D2); routes this read to
      // app74/app21 server-side (ADR-008 D3).
      request: { referenceId: winner.referenceId, is_demo: isDemoOf(identity) },
      ...(signal ? { signal } : {}),
    }),
  );
  if (enrichRec.kind === "halt") {
    onStage?.("enrich", "failed");
    return enrichRec.outcome;
  }
  const meta = enrichRec.response;
  if (meta.resolved === false) {
    // A reference id that SHOULD resolve but does not → halt (ADR-004 D5).
    onStage?.("enrich", "failed");
    return { kind: "halted", reason: "meta_resolution_failed" };
  }
  onStage?.("enrich", "done");

  // `deploymentChannel` is the producing/updater AU's own full name (ADR-004
  // D4), known from the D1 winner candidate, not from D2's meta enrichment —
  // FirmwareDeliveryAPIv1 has no notion of which updater delivered the
  // package it produced. Override rather than trust the enrich response for
  // this one field (ISO/IEC 5055 — do not blur the two layers' provenance).
  const operation: SelectedOperation = {
    meta: { ...meta.meta, deploymentChannel: winner.deploymentChannel },
    execution: winner.execution,
  };

  // D6 only triggers when AU0001 wins D3 (handled above); an
  // AU0008/AU0004-sourced winner never runs the offline stage.
  onStage?.("offline", "skipped");
  return { kind: "resolved_shown", identity, operation, queriedUpdaterAUs: allQueriedAUs };
}

// ── internals ───────────────────────────────────────────────────────────────

/**
 * One timed input to ADR-004 D3's comparison: either an AU0008/AU0004
 * candidate (ordered by its own Execution Time) or the unconditional AU0001
 * read (ordered by its own `operation_ts`), amended by #35.
 */
type TimedInput =
  | { readonly source: "updater"; readonly candidate: CandidateOperation; readonly timestampMs: number }
  | { readonly source: "au0001"; readonly timestampMs: number };

/**
 * ADR-004 D3 (amended by #35): among the D1 candidates and the unconditional
 * AU0001 read, the highest timestamp wins — no meta enrichment needed to
 * select. Only D1-eligible updater candidates (already filtered upstream) and
 * a successful AU0001 read compete (the D1/D3 eligibility filter). Returns
 * `undefined` when nothing is eligible (ADR-004 D1 empty result).
 *
 * Tie-break (equal timestamps) is deterministic: first-seen order is
 * preserved (stable `reduce`) and reported via the returned `tied` flag
 * (ISO/IEC 5055 — no silent, unreported tie resolution).
 */
function selectLastOperation(
  candidates: readonly CandidateOperation[],
  au0001: OfflineCandidateResponse,
): { readonly source: "updater"; readonly candidate: CandidateOperation; readonly tied: boolean }
  | { readonly source: "au0001"; readonly tied: boolean }
  | undefined {
  const timed: TimedInput[] = [];

  for (const candidate of candidates) {
    const ms = parseTimestamp(candidate.executionTime);
    if (ms !== undefined) {
      timed.push({ source: "updater", candidate, timestampMs: ms });
    }
  }

  if (au0001.found && au0001.executionStatus === AU0001_SUCCESS_LITERAL) {
    const ms = parseTimestamp(au0001.operationTs);
    if (ms !== undefined) {
      timed.push({ source: "au0001", timestampMs: ms });
    }
  }

  if (timed.length === 0) return undefined;

  const best = timed.reduce((acc, current) =>
    current.timestampMs > acc.timestampMs ? current : acc,
  );
  const tied = timed.filter((t) => t.timestampMs === best.timestampMs).length > 1;

  return best.source === "updater"
    ? { source: "updater", candidate: best.candidate, tied }
    : { source: "au0001", tied };
}

function parseTimestamp(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? undefined : ms;
}

function softwareIdOf(identity: {
  softwareId: string | { unknown: true };
}): string {
  return typeof identity.softwareId === "string" ? identity.softwareId : "";
}

/**
 * `is_demo` as carried forward to the FirmwareDelivery requests (ADR-008 D2).
 * An unknown flag (should not happen once resolve succeeds — the resolver
 * always returns it on the success branches) is treated as `false` rather
 * than thrown here; the server's own `is_demo_not_provided` guard is the
 * authoritative check (ADR-008 D3), this is only the client-side carry.
 */
function isDemoOf(identity: { isDemo: boolean | { unknown: true } }): boolean {
  return isUnknown(identity.isDemo) ? false : identity.isDemo;
}

/**
 * Normalize a stage's terminal bridge record into either its typed response or
 * a mapped halt outcome (ADR-002 P5 branch on terminal state; ADR-006 D4).
 */
type StageResult<TResponse> =
  | { kind: "ok"; response: TResponse }
  | { kind: "halt"; outcome: QueryOutcome };

/**
 * Whether a stage record's raw `first_error` carries `reason` as a
 * substring, not necessarily the whole string — the real transport wraps an
 * Error-status stage record's resolver reason as
 * "<BridgeErrorKind>: <first_error>" (zoho-transport.ts), so a resolver-set
 * literal like "duplicate_device" or "unauthorized" never arrives as an
 * exact match. Case-sensitive on purpose: these are fixed resolver literals
 * (ProcessServiceRequest.ds), not free text to fuzzy-match.
 */
function containsFirstError(firstError: string | undefined, reason: string): boolean {
  return firstError !== undefined && firstError.includes(reason);
}

async function submit<TResponse>(
  _stage: StageId,
  run: () => Promise<{ status: string; response?: TResponse; firstError?: string }>,
): Promise<StageResult<TResponse>> {
  try {
    const rec = await run();
    if (rec.status === "Success" && rec.response !== undefined) {
      return { kind: "ok", response: rec.response };
    }
    // Error terminal state (P5). first_error rarely arrives as the bare
    // resolver literal (e.g. "duplicate_device") — the real transport wraps
    // an Error-status stage record's first_error as
    // "<BridgeErrorKind>: <first_error>" (zoho-transport.ts), so a raw
    // resolver reason is a SUBSTRING of rec.firstError, never the whole
    // string. Every named resolver reason below is therefore matched by
    // containment, not `===`, mirroring the device_not_found check this
    // pattern was first written for — an exact-match check here would never
    // fire against the wrapped real value (PM-caught on #57: duplicate_device
    // wasn't triggering the halt at all).
    //
    // "unauthorized" maps to the ADR-006 D5 outcome.
    if (containsFirstError(rec.firstError, "unauthorized")) {
      return { kind: "halt", outcome: { kind: "halted", reason: "unauthorized" } };
    }
    // "multiple_devices_found" is the Device Registry's OWN error (app21 or
    // app74 matching more than one record for the identifier within that
    // ONE registry) — surfaced via the ADR-008 "problem" branch's
    // first_error relay (ProcessServiceRequest.ds), not a literal this
    // resolver writes itself. Distinct from ADR-008 D1's `duplicate_device`
    // (the SAME device found in BOTH registries): this is ADR-003 D3's
    // single-registry ambiguous-match case, and reuses that existing
    // "ambiguous" halt reason (already wired for the resolve stage's own
    // `ambiguous: true` flag) rather than falling through to a generic,
    // detail-leaking bridge_error.
    if (containsFirstError(rec.firstError, "multiple_devices_found")) {
      return { kind: "halt", outcome: { kind: "halted", reason: "ambiguous" } };
    }
    // Both registries returned the identifier (ADR-008 D1) — a defined,
    // user-surfaced error, deliberately NOT folded into the non-disclosing
    // not_found_or_out_of_scope / ambiguous paths (ADR-003 D3 stays in force
    // for its own cases only).
    if (containsFirstError(rec.firstError, "duplicate_device")) {
      return { kind: "halt", outcome: { kind: "halted", reason: "duplicate_device" } };
    }
    // not_found_or_out_of_scope surfaces here as a raw resolver failure
    // (e.g. "system_error: not_found_or_out_of_scope") on an Error-status
    // stage record — ProcessServiceRequest.ds's own `throws
    // "not_found_or_out_of_scope"` when neither registry cleanly finds the
    // device (ADR-008 D1 point 4), caught by the branch's catch(e) and
    // written onto first_error verbatim. PM-caught on #57: this used to
    // check for the substring "device_not_found" instead — a literal that
    // predates ADR-008's resolve-and-route (the old single-registry code
    // could relay the Device Registry's own success:false `error` field,
    // literally "device_not_found", as first_error) and that
    // ProcessServiceRequest.ds no longer ever writes to first_error (the
    // "problem" branch added by #57 explicitly EXCLUDES a clean
    // device_not_found from being reported as first_error at all — see its
    // app21_problem/app74_problem derivation). The check below never
    // matched the resolver's actual literal, so a genuine not-found fell
    // through to the generic, detail-leaking bridge_error halt instead of
    // ADR-003 D3 / ADR-006 D3's non-disclosing "no display" outcome.
    if (containsFirstError(rec.firstError, "not_found_or_out_of_scope")) {
      return { kind: "halt", outcome: { kind: "not_found_or_out_of_scope" } };
    }
    return {
      kind: "halt",
      outcome: {
        kind: "halted",
        reason: "bridge_error",
        ...(rec.firstError ? { detail: rec.firstError } : {}),
      },
    };
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return { kind: "halt", outcome: { kind: "halted", reason: "bridge_timeout" } };
    }
    return {
      kind: "halt",
      outcome: {
        kind: "halted",
        reason: "bridge_error",
        detail: err instanceof Error ? err.message : String(err),
      },
    };
  }
}
